/**
 * Imágenes que se adjuntan a un mensaje para Sol: desde el clip, pegando una
 * captura o arrastrando. Tope de 5, solo JPEG/PNG/WebP/GIF, reducción a 2000 px
 * en el navegador. Un archivo que no sirve se rechaza con un aviso y no se agrega.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
} from 'react';
import { toast } from 'sonner';

import {
  MAX_BASE64_TOTAL,
  MAX_IMAGENES,
  motivoDeRechazo,
  prepararImagen,
  totalBase64,
  type ImagenPreparada,
} from '../lib/adjuntos';

export interface ImagenAdjunta extends ImagenPreparada {
  id: string;
  nombre: string;
  /** Miniatura: URL de objeto del archivo original. */
  vistaPrevia: string;
}

interface Opciones {
  /** Inyectable para los tests (el canvas no existe en jsdom). */
  preparar?: (archivo: File) => Promise<ImagenPreparada>;
  /** Mientras es true (se está enviando) pegar o arrastrar no agrega nada. */
  bloqueado?: boolean;
}

let contador = 0;

export function useImagenesAdjuntas({ preparar = prepararImagen, bloqueado = false }: Opciones = {}) {
  const [imagenes, setImagenes] = useState<ImagenAdjunta[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  // La lista vive en un ref y el estado la refleja: así los efectos (revocar
  // URLs, cupo) no corren dentro de un updater, que StrictMode ejecuta dos veces.
  const lista = useRef<ImagenAdjunta[]>([]);
  // Cupo ocupado, incluidas las que todavía se están reduciendo: dos pegados
  // seguidos no pueden pasarse del tope.
  const ocupadas = useRef(0);
  const enCurso = useRef(0);
  const esperando = useRef<(() => void)[]>([]);
  const nivelDrag = useRef(0);

  const fijar = useCallback((siguiente: ImagenAdjunta[]) => {
    lista.current = siguiente;
    setImagenes(siguiente);
  }, []);

  useEffect(
    () => () => {
      lista.current.forEach((u) => URL.revokeObjectURL(u.vistaPrevia));
    },
    [],
  );

  const agregar = useCallback(
    async (archivos: Iterable<File>) => {
      if (bloqueado) return;
      const archivosLista = Array.from(archivos);
      if (archivosLista.length === 0) return;
      const aceptados: File[] = [];
      let superoElTope = false;
      for (const f of archivosLista) {
        const motivo = motivoDeRechazo(f);
        if (motivo) {
          toast.error('No se pudo adjuntar', { description: motivo });
          continue;
        }
        if (ocupadas.current + aceptados.length >= MAX_IMAGENES) {
          superoElTope = true;
          continue;
        }
        aceptados.push(f);
      }
      if (superoElTope) {
        toast.error('Máximo de imágenes', {
          description: `Podés adjuntar hasta ${MAX_IMAGENES} imágenes por mensaje.`,
        });
      }
      if (aceptados.length === 0) return;
      ocupadas.current += aceptados.length;
      enCurso.current += aceptados.length;
      setProcesando(true);
      await Promise.all(
        aceptados.map(async (f) => {
          try {
            const p = await preparar(f);
            if (totalBase64(lista.current) + p.base64.length > MAX_BASE64_TOTAL) {
              ocupadas.current -= 1;
              toast.error('Las imágenes pesan demasiado', {
                description: `No se agregó ${f.name || 'la imagen'}: el total del mensaje pasaría de ${Math.round(MAX_BASE64_TOTAL / 1024 / 1024)} MB.`,
              });
              return;
            }
            fijar([
              ...lista.current,
              {
                ...p,
                id: `img-${++contador}`,
                nombre: f.name || 'captura',
                vistaPrevia: URL.createObjectURL(f),
              },
            ]);
          } catch {
            ocupadas.current -= 1;
            toast.error('No se pudo adjuntar', {
              description: 'No se pudo leer la imagen.',
            });
          } finally {
            enCurso.current -= 1;
            if (enCurso.current === 0) {
              setProcesando(false);
              esperando.current.splice(0).forEach((r) => r());
            }
          }
        }),
      );
    },
    [preparar, bloqueado, fijar],
  );

  /** Se resuelve cuando no queda ninguna imagen reduciéndose. */
  const esperarProcesado = useCallback(
    () =>
      enCurso.current === 0
        ? Promise.resolve()
        : new Promise<void>((resolver) => esperando.current.push(resolver)),
    [],
  );

  const quitar = useCallback(
    (id: string) => {
      const sale = lista.current.find((i) => i.id === id);
      if (!sale) return;
      URL.revokeObjectURL(sale.vistaPrevia);
      ocupadas.current -= 1;
      fijar(lista.current.filter((i) => i.id !== id));
    },
    [fijar],
  );

  /** Quita las imágenes indicadas (las que viajaron); sin ids, todas. */
  const limpiar = useCallback(
    (ids?: string[]) => {
      const sacar = ids ? new Set(ids) : null;
      const salen = lista.current.filter((i) => !sacar || sacar.has(i.id));
      if (salen.length === 0) return;
      salen.forEach((i) => URL.revokeObjectURL(i.vistaPrevia));
      ocupadas.current -= salen.length;
      fijar(lista.current.filter((i) => !salen.includes(i)));
    },
    [fijar],
  );

  const alPegar = useCallback(
    (e: ClipboardEvent) => {
      const archivos = Array.from(e.clipboardData?.files ?? []).filter((f) =>
        f.type.startsWith('image/'),
      );
      if (archivos.length === 0) return;
      e.preventDefault();
      void agregar(archivos);
    },
    [agregar],
  );

  const tieneArchivos = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const propsDeArrastre = {
    onDragEnter: (e: DragEvent) => {
      if (!tieneArchivos(e)) return;
      e.preventDefault();
      nivelDrag.current += 1;
      setArrastrando(true);
    },
    onDragOver: (e: DragEvent) => {
      if (tieneArchivos(e)) e.preventDefault();
    },
    onDragLeave: (e: DragEvent) => {
      if (!tieneArchivos(e)) return;
      nivelDrag.current = Math.max(0, nivelDrag.current - 1);
      if (nivelDrag.current === 0) setArrastrando(false);
    },
    onDrop: (e: DragEvent) => {
      if (!tieneArchivos(e)) return;
      e.preventDefault();
      e.stopPropagation();
      nivelDrag.current = 0;
      setArrastrando(false);
      void agregar(Array.from(e.dataTransfer.files));
    },
  };

  /** Lo que viaja en `images` del pedido. */
  const paraEnviar = useCallback(
    () => lista.current.map(({ mimeType, base64 }) => ({ mimeType, base64 })),
    [],
  );
  /** Ids de lo que viaja, en el mismo orden que `paraEnviar`. */
  const idsParaEnviar = useCallback(() => lista.current.map((i) => i.id), []);

  return {
    imagenes,
    procesando,
    arrastrando,
    agregar,
    quitar,
    limpiar,
    alPegar,
    propsDeArrastre,
    paraEnviar,
    idsParaEnviar,
    esperarProcesado,
  };
}

export type ImagenesAdjuntas = ReturnType<typeof useImagenesAdjuntas>;
